import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Status } from 'src/app/pages/ip-whitelisting/ip-whitelisting.component';
import { ApiControllerService } from '../../api-controller-service';
import { HttpErrorResponse } from '@angular/common/http';
import { parseHttpErrorMessage } from 'src/app/_helpers/http-error.utils';

export interface IPWhitelistFormData {
  id?: string;
  ipAddress: string;
  name?: string;
  status: Status;
  merchantId?: string;
  merchantName?: string;
  version?: number;
}

export type DialogMode = 'add' | 'edit';


@Component({
  selector: 'app-ip-whitelist-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ip-whitelist-dialog.component.html',
  styleUrls: ['./ip-whitelist-dialog.component.css']
})
export class IpWhitelistDialogComponent implements OnInit, OnChanges {

  constructor(private readonly apiService: ApiControllerService) {}
  @Input() isVisible: boolean = false;
  @Input() mode: DialogMode = 'add';
  @Input() ipData: IPWhitelistFormData | null = null;
  @Input() showMerchantSearch: boolean = false;
  @Input() showMerchantInfo: boolean = false;
  @Input() merchantName: string = '';
  @Input() merchantId: string = '';
  
  @Output() isVisibleChange = new EventEmitter<boolean>();
  @Output() closed = new EventEmitter<void>();
  @Output() success = new EventEmitter<void>();
  @Output() failed = new EventEmitter<string>();

  formData: IPWhitelistFormData = {
    ipAddress: '',
    status: Status.ACTIVE,
    version: 0
  };
  resolvedMerchantName: string = '';

  ipAddressError: string = '';
  isSubmitting: boolean = false;

  // Merchant search state (PAY10 Admin add mode)
  merchantQuery: string = '';
  isValidatingMerchant: boolean = false;
  merchantError: string = '';
  validatedMerchant: { merchantId: string; merchantName: string } | null = null;

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isVisible) {
      this.initializeForm();
    }
  }

  private initializeForm(): void {
    this.ipAddressError = '';
    this.isSubmitting = false;
    this.isValidatingMerchant = false;
    this.merchantError = '';
    this.resolvedMerchantName = this.merchantName || '';
    if (this.mode === 'edit' && this.ipData) {
      this.merchantQuery = '';
      this.validatedMerchant = null;
      this.formData = {
        id: this.ipData.id,
        ipAddress: this.ipData.ipAddress,
        name: this.ipData.name || undefined,
        status: Status.ACTIVE,
        merchantId: this.ipData.merchantId || undefined,
        merchantName: this.ipData.merchantName || undefined,
        version: this.ipData.version
      };
      // Resolve merchant name from backend if only ID is present
      if (this.ipData.merchantId && !this.ipData.merchantName) {
        this.apiService.findMerchant(this.ipData.merchantId).subscribe({
          next: (resp: any) => {
            const data = resp?.response?.data ?? resp?.data ?? null;
            this.resolvedMerchantName = data?.merchantName || '';
          },
          error: () => { /* silently fail, display will fall back to ID */ }
        });
      }
    } else {
      this.formData = {
        ipAddress: '',
        status: Status.ACTIVE
      };
      // Create mode: reuse the merchant already searched on the main page
      if (this.merchantId) {
        this.formData.merchantId = this.merchantId;
        this.merchantQuery = this.merchantDisplay;
        this.validatedMerchant = { merchantId: this.merchantId, merchantName: this.merchantName };
      } else {
        this.merchantQuery = '';
        this.validatedMerchant = null;
      }
    }
  }

  get dialogTitle(): string {
    return this.mode === 'add' ? 'Add Whitelisted IP Addresss' : 'Edit Whitelisted IP Address';
  }

  get dialogDescription(): string {
    return 'To add Whitelisted APIs, specify the endpoints your app will use. This management ensures smooth data flow and functionality.';
  }

  get submitButtonText(): string {
    return this.mode === 'add' ? 'Continue' : 'Save Changes';
  }

  get merchantDisplay(): string {
    const name = (this.resolvedMerchantName || this.merchantName || '').trim();
    const id = (this.formData.merchantId || '').trim();
    if (name && id) return `${name} (${id})`;
    return name || id || '-';
  }

  close(): void {
    this.isVisible = false;
    this.isVisibleChange.emit(false);
    this.closed.emit();
    this.resetForm();
  }

  handleSubmit(): void {
    if (!this.isFormValid() || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.ipAddressError = '';

    const apiCall = this.mode === 'add' 
      ? this.apiService.createIPWhitelist(this.formData)
      : this.apiService.updateIPWhitelist(this.formData);

    apiCall.subscribe({
      next: (response: any) => {
        this.isSubmitting = false;
        // Check if response has error status or message indicating failure
        if (response && (response.status >= 400 || response.message)) {
          // API returned an error
          this.ipAddressError = response.message || 'An error occurred while processing your request';
        } else {
          // Success
          this.success.emit();
          this.close();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        if(error?.status === 400) {
          this.ipAddressError = parseHttpErrorMessage(error, 'Invalid format IP Address or IP already exists');
        } else {
          this.failed.emit(parseHttpErrorMessage(error, 'An error occurred while processing your request'));
          this.close();
        }
      }
    });
  }

  handleCancel(): void {
    this.close();
  }

  handleBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  validateIPAddress(): void {
    const ipAddress = this.formData.ipAddress.trim();
    
    if (!ipAddress) {
      this.ipAddressError = '';
      return;
    }

    // IP address regex pattern: {0-255}.{0-255}.{0-255}.{0-255}
    const ipPattern = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
    const match = ipPattern.exec(ipAddress);

    if (!match) {
      this.ipAddressError = 'Invalid IP address format. Expected format: \\d{1,3}.\\d{1,3}.\\d{1,3}.\\d{1,3}';
      return;
    }

    // Check each octet is between 0-255
    const octets = [match[1], match[2], match[3], match[4]];
    for (const octet of octets) {
      const num = Number.parseInt(octet, 10);
      if (num < 0 || num > 255) {
        this.ipAddressError = 'Each number in the IP address must be between 0 and 255';
        return;
      }
    }

    this.ipAddressError = '';
  }

  isFormValid(): boolean {
    const ipValid = this.formData.ipAddress.trim().length > 0 && !this.ipAddressError;
    if (this.mode === 'add' && this.showMerchantSearch) {
      return ipValid && !!this.validatedMerchant;
    }
    return ipValid;
  }

  searchMerchant(): void {
    const query = (this.merchantQuery || '').trim();
    if (!query || this.isValidatingMerchant) return;

    this.isValidatingMerchant = true;
    this.merchantError = '';
    this.validatedMerchant = null;

    this.apiService.findMerchant(query).subscribe({
      next: (resp: any) => {
        this.isValidatingMerchant = false;
        const data = resp?.response?.data ?? resp?.data ?? null;
        if (data && data.merchantId) {
          this.validatedMerchant = { merchantId: data.merchantId, merchantName: data.merchantName };
          this.formData.merchantId = this.validatedMerchant.merchantId;
        } else {
          this.merchantError = 'No merchant found. Please enter a valid merchant name or ID.';
        }
      },
      error: (err: HttpErrorResponse) => {
        this.isValidatingMerchant = false;
        console.error('Error finding merchant:', err);
        this.merchantError = 'No merchant found. Please enter a valid merchant name or ID.';
      }
    });
  }

  clearMerchantSearch(): void {
    this.merchantQuery = '';
    this.merchantError = '';
    this.validatedMerchant = null;
    this.formData.merchantId = undefined;
  }

  private resetForm(): void {
    this.formData = {
      id: '',
      ipAddress: '',
      name: '',
      status: Status.ACTIVE,
      version: 0
    };
    this.ipAddressError = '';
    this.isSubmitting = false;
    this.merchantQuery = '';
    this.merchantError = '';
    this.validatedMerchant = null;
  }
}